// src/Notes.jsx
import React, { useState, useEffect } from "react";
import { useAuth } from "./lib/AuthContext";
import jsPDF from "jspdf";

const Notes = () => {
  const { user, accessToken } = useAuth();
  const [format, setFormat] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [fields, setFields] = useState({
    subjective: "",
    objective: "",
    assessment: "",
    plan: "",
    data: "",
    freeform: "",
    checklist: {
      consent: false,
      risk: false,
      boundaries: false,
    },
  });
  const [savedNotes, setSavedNotes] = useState([]);

  useEffect(() => {
    if (!accessToken) return;
    fetch("https://quirkleai.com/api/sessions/", {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => res.json())
      .then(data => setSessions(data))
      .catch(err => console.error("Failed to fetch sessions", err));
  }, [accessToken]);

  useEffect(() => {
    if (!selectedSession || !accessToken) return;
    fetch(`https://quirkleai.com/api/notes/?session=${selectedSession}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(res => res.json())
      .then(data => setSavedNotes(Array.isArray(data) ? data : []))
      .catch(err => console.error("Failed to fetch notes", err));
  }, [selectedSession, accessToken]);

  const handleChange = (field, value) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleChecklist = (key) => {
    setFields((prev) => ({
      ...prev,
      checklist: { ...prev.checklist, [key]: !prev.checklist[key] },
    }));
  };

  const handleDuplicate = () => {
    if (!savedNotes.length) return alert("No previous notes to duplicate.");
    const last = savedNotes[savedNotes.length - 1];
    if (!last || !last.content) return;

    if (last.format === "soap") {
      const match = last.content.match(/S:([\s\S]*)O:([\s\S]*)A:([\s\S]*)P:([\s\S]*)/);
      if (match) {
        setFields({
          ...fields,
          subjective: match[1].trim(),
          objective: match[2].trim(),
          assessment: match[3].trim(),
          plan: match[4].trim(),
        });
      }
    } else if (last.format === "dap") {
      const match = last.content.match(/D:([\s\S]*)A:([\s\S]*)P:([\s\S]*)/);
      if (match) {
        setFields({
          ...fields,
          data: match[1].trim(),
          assessment: match[2].trim(),
          plan: match[3].trim(),
        });
      }
    } else {
      setFields({ ...fields, freeform: last.content });
    }
  };

  const handleDownloadPDF = (note) => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Quirkle Session Note`, 10, 10);
    doc.setFontSize(10);
    doc.text(`Session ID: ${note.session || '-'} | Format: ${note.format.toUpperCase()}`, 10, 18);
    doc.text(`Author: ${user.username}`, 10, 26);

    const lines = doc.splitTextToSize(note.content, 180);
    doc.text(lines, 10, 36);
    doc.save(`note_session_${note.session}.pdf`);
  };

  const renderForm = () => {
    switch (format) {
      case "soap":
        return (
          <div className="space-y-4">
            <textarea placeholder="Subjective" className="w-full border p-2" value={fields.subjective} onChange={(e) => handleChange("subjective", e.target.value)} />
            <textarea placeholder="Objective" className="w-full border p-2" value={fields.objective} onChange={(e) => handleChange("objective", e.target.value)} />
            <textarea placeholder="Assessment" className="w-full border p-2" value={fields.assessment} onChange={(e) => handleChange("assessment", e.target.value)} />
            <textarea placeholder="Plan" className="w-full border p-2" value={fields.plan} onChange={(e) => handleChange("plan", e.target.value)} />
          </div>
        );
      case "dap":
        return (
          <div className="space-y-4">
            <textarea placeholder="Data" className="w-full border p-2" value={fields.data} onChange={(e) => handleChange("data", e.target.value)} />
            <textarea placeholder="Assessment" className="w-full border p-2" value={fields.assessment} onChange={(e) => handleChange("assessment", e.target.value)} />
            <textarea placeholder="Plan" className="w-full border p-2" value={fields.plan} onChange={(e) => handleChange("plan", e.target.value)} />
          </div>
        );
      case "freeform":
        return (
          <div className="space-y-4">
            <textarea placeholder="Write your session note..." className="w-full border p-2 h-48" value={fields.freeform} onChange={(e) => handleChange("freeform", e.target.value)} />
          </div>
        );
      default:
        return null;
    }
  };

  const handleSave = () => {
    if (!selectedSession || !format) {
      alert("Please select a session and format.");
      return;
    }

    const content =
      format === "soap"
        ? `S: ${fields.subjective}\nO: ${fields.objective}\nA: ${fields.assessment}\nP: ${fields.plan}`
        : format === "dap"
        ? `D: ${fields.data}\nA: ${fields.assessment}\nP: ${fields.plan}`
        : fields.freeform;

    if (!content.trim()) {
      alert("Note content is empty. Please write something.");
      return;
    }

    const payload = {
      session: selectedSession,
      format,
      content,
    };

    fetch("https://quirkleai.com/api/notes/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(payload),
    })
      .then((res) => {
        if (!res.ok) throw new Error("Save failed");
        alert("Note saved successfully.");
      })
      .catch((err) => {
        alert("Error saving note.");
        console.error(err);
      });
  };

  return (
    <div className="space-y-6">
      {!format && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button onClick={() => setFormat("soap")} className="p-4 border rounded shadow hover:bg-blue-50">
            <strong>SOAP Format</strong>
            <p className="text-sm">Structured medical-style notes</p>
          </button>
          <button onClick={() => setFormat("dap")} className="p-4 border rounded shadow hover:bg-blue-50">
            <strong>DAP Format</strong>
            <p className="text-sm">Data, Assessment, Plan</p>
          </button>
          <button onClick={() => setFormat("freeform")} className="p-4 border rounded shadow hover:bg-blue-50">
            <strong>Freeform</strong>
            <p className="text-sm">Flexible & prompt-guided</p>
          </button>
        </div>
      )}

      {format && (
        <div className="space-y-4">
          <label className="block text-sm font-medium">Select Session</label>
          <select
            className="w-full border p-2 rounded"
            value={selectedSession || ""}
            onChange={(e) => setSelectedSession(e.target.value)}
          >
            <option value="">-- Choose session --</option>
            {sessions.map((s) => (
              <option key={s.id} value={s.id}>
                {`Session ${s.id} - ${s.client?.username || "Client"} on ${new Date(s.start_time).toLocaleString()}`}
              </option>
            ))}
          </select>

          <h2 className="text-lg font-semibold capitalize">{format} Notes</h2>
          {renderForm()}

          <div className="flex space-x-4 mt-4">
            <button onClick={handleSave} className="bg-green-600 text-white px-4 py-2 rounded">
              Save Note
            </button>
            <button onClick={handleDuplicate} className="bg-yellow-500 text-white px-4 py-2 rounded">
              📋 Duplicate Last Note
            </button>
            <button onClick={() => setFormat(null)} className="text-gray-500 underline">
              Change Format
            </button>
          </div>

          <div className="mt-8">
            <h3 className="text-lg font-bold mb-2">Saved Notes for Session</h3>
            {savedNotes.length === 0 && <p className="text-gray-500">No notes saved yet.</p>}
            {savedNotes.map((note) => (
              <div key={note.id} className="border rounded p-4 mb-3 bg-gray-50">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600">Format: {note.format.toUpperCase()}</div>
                  <button
                    onClick={() => handleDownloadPDF(note)}
                    className="text-sm text-blue-600 underline"
                  >
                    Download PDF
                  </button>
                </div>
                <pre className="whitespace-pre-wrap text-gray-800 text-sm mt-2">{note.content}</pre>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Notes;
